const API_BASE_URL = process.env.REACT_APP_BASE_URL

export type MoneyAmount = {
  amount: number
  currency: string
}

export type InventoryFareBasis = {
  id: number
  flight_id: number
  segment_id: number
  cabin: string
  class_of_service: string
  fare_basis_code: string
  brand_code?: string | null
  brand_name?: string | null
}

export type InventoryFlight = {
  flight_id: number
  flight_order: number
  departure_datetime: string
  arrival_datetime: string
  flight_number: string
  carrier: string
  operating_carrier?: string | null
  operating_carrier_name?: string | null
  departure_airport: string
  arrival_airport: string
  duration_min: number
  aircraft_type?: string | null
  fare_basis?: InventoryFareBasis | null
}

export type InventorySegment = {
  segment_id: number
  segment_order: number
  direction?: 'outbound' | 'inbound' | string | null
  flights: InventoryFlight[] | null
}

export type InventoryTax = {
  id: number
  code: string
  tax_type?: string | null
  description?: string | null
  amount: MoneyAmount
}

export type InventoryFare = {
  id: number
  passenger_type: string
  passenger_code: string
  total_amount: MoneyAmount
  base_amount: MoneyAmount
  total_taxes: MoneyAmount
  package_only_markup?: MoneyAmount | null
  air_only_markup?: MoneyAmount | null
  taxes?: InventoryTax[] | null
}

/** Baggage allowance stored as jsonb on the itinerary, e.g. {"quantity": 1}. */
export type BaggageAllowance = {
  quantity?: number | null
}

/** Per-channel distribution flags and agency lists (GIGI / API). */
export type DistributionFields = {
  distribute_gigi?: boolean | null
  distribute_api?: boolean | null
  gigi_agency_include?: string[] | null
  gigi_agency_exclude?: string[] | null
  api_agency_include?: string[] | null
  api_agency_exclude?: string[] | null
}

export type InventoryBlockSummary = DistributionFields & {
  last_ticketable_date?: string | null
  seat_release_date?: string | null
  total_seats_blocked?: number | null
  total_seats_booked?: number | null
  booking_source?: string | null
  block_source?: string | null
  source?: 'GIGI' | 'API' | string | null
  multiple_pnrs?: string | null
  agency_include?: string[] | null
  agency_exclude?: string[] | null
  carry_on_baggage?: BaggageAllowance | null
  free_baggage?: BaggageAllowance | null
}

export type InventoryItinerary = DistributionFields & {
  id: number
  fare_type: string
  plating_carrier: string
  validating_carrier?: string | null
  last_ticketable_date: string | null
  carry_on_baggage?: BaggageAllowance | null
  free_baggage?: BaggageAllowance | null
  total_seats_blocked?: number | null
  total_seats_booked?: number | null
  multiple_pnrs?: string | null
  block_source?: string | null
  booking_source?: string | null
  source?: 'GIGI' | 'API' | string | null
  agency_include?: string[] | null
  agency_exclude?: string[] | null
  seat_release_date?: string | null
  fare_rules_en?: string | null
  fare_rules_fr?: string | null
  is_active?: boolean
  created_at: string
  updated_at: string
  booking_count?: number | null
  segments: InventorySegment[] | null
  outbound?: InventorySegment | null
  inbound?: InventorySegment | null
  fare_basis: InventoryFareBasis[] | null
  fares: InventoryFare[] | null
  summary?: InventoryBlockSummary | null
  tripType?: string
}

export type InventoryResponse = {
  count: number
  data: InventoryItinerary[]
}

export type PaxFareSnapshot = {
  taxes?: unknown[]
  baseAmount?: MoneyAmount
  totalTaxes?: MoneyAmount
  totalAmount?: MoneyAmount
  passengerDetails?: {
    code?: string
    type?: string
    quantity?: string | number
  }
  passengerType?: string
  passengerTypeCode?: string
  quantity?: number
}

export type PassengerDetails = {
  prefix?: string
  firstName?: string
  middleName?: string
  lastName?: string
  gender?: string
  dateOfBirth?: string
  paxType?: string
  passengerType?: string
  passengerTypeCode?: string
  nationality?: string
  contact?: {
    emails?: Array<string | { email?: string; type?: string }>
    phones?: Array<string | { number?: string; type?: string }>
  }
}

export type BookingPayment = {
  id: number
  booking_id: string
  pax_id: number
  payment_details: {
    type?: string
    paymentType?: string
    paymentDetails?: string
    totalAmount?: MoneyAmount
    passengerRefId?: string
  } | null
  created_at: string
  updated_at: string
}

export type BookingPassenger = {
  id: number
  booking_id: string
  pax_details: PassengerDetails | null
  pax_booking_status: string | null
  airline_record_locator: string | null
  ticket_number: string | null
  created_at: string
  updated_at: string
  payments: BookingPayment[] | null
}

export type InventoryBooking = {
  booking_id: string
  itinerary_id: number | null
  pax_fare: PaxFareSnapshot[] | null
  created_at: string
  updated_at: string
  itinerary: {
    id: number
    fare_type: string
    plating_carrier: string
    last_ticketable_date: string | null
  } | null
  passengers: BookingPassenger[] | null
}

export type InventorySearchParams = {
  id?: number
  departureAirport?: string
  arrivalAirport?: string
  departureDateFrom?: string
  departureDateTo?: string
  carriers?: string[]
  cabins?: string[]
  fareType?: string
  platingCarriers?: string[]
  flightNumber?: string
  classesOfService?: string[]
  brandCodes?: string[]
  fareBasisCode?: string
  lastTicketableFrom?: string
  lastTicketableTo?: string
}

export type FlightsData = {
  id: number
  itinerary_id: number
  segment_id: number
  flight_order: number
  flight_number: string
  carrier: string
  operating_carrier?: string | null
  operating_carrier_name?: string | null
  departure_airport: string
  departure_datetime: string
  arrival_airport: string
  arrival_datetime: string
  aircraft_type?: string | null
  duration_min: number
  created_at?: string
  updated_at?: string
}

/** Distribution payload: one flag per channel plus its agency lists. */
export type DistributionPayload = {
  distributeGigi?: boolean
  distributeApi?: boolean
  gigiAgencyInclude?: string | string[] | null
  gigiAgencyExclude?: string | string[] | null
  apiAgencyInclude?: string | string[] | null
  apiAgencyExclude?: string | string[] | null
}

export type CreateInventoryPayload = {
  tripType: 'OW' | 'RT'
  itinerary: DistributionPayload & {
    platingCarrier?: string
    validatingCarrier?: string
    fareType: string
    lastTicketableDate: string
    totalSeatsBlocked?: number | null
    totalSeatsBooked?: number | null
    multiplePnrs?: string | string[] | null
    blockSource?: string | null
    bookingSource?: string | null
    source?: 'GIGI' | 'API' | null
    agencyInclude?: string | string[] | null
    agencyExclude?: string | string[] | null
    seatReleaseDate?: string | null
    fareRulesEn?: string | null
    fareRulesFr?: string | null
    carryOnBaggage?: BaggageAllowance | null
    freeBaggage?: BaggageAllowance | null
  }
  /** Optional Blocks Summary tab payload — merged into itinerary on the backend. */
  summary?: DistributionPayload & {
    lastTicketableDate?: string
    seatReleaseDate?: string | null
    totalSeatsBlocked?: number | null
    totalSeatsBooked?: number | null
    bookingSource?: string | null
    blockSource?: string | null
    source?: 'GIGI' | 'API' | null
    multiplePnrs?: string | string[] | null
    agencyInclude?: string | string[] | null
    agencyExclude?: string | string[] | null
  }
  pricing: Array<{
    currency: string
    baseAmount: number
    totalTaxes: number
    packageOnlyMarkup?: number | null
    airOnlyMarkup?: number | null
    passengerType: string
    passengerCode: string
  }>
  segments: Array<{
    segmentOrder: number
    direction?: 'outbound' | 'inbound'
    flights: Array<{
      flightNumber: string
      carrier: string
      operatingCarrier?: string
      operatingCarrierName?: string
      departureAirport: string
      departureDatetime: string
      arrivalAirport: string
      arrivalDatetime: string
      aircraftType?: string
      durationMin: number
      flightOrder: number
      fareBasis: {
        cabin: string
        classOfService?: string
        fareBasisCode?: string
        brandCode?: string
        brandName?: string
      }
    }>
  }>
}

function toQuery(params: Record<string, unknown> = {}): string {
  const search = new URLSearchParams()

  Object.entries(params).forEach(([key, value]) => {
    if (value == null || value === '') return
    if (Array.isArray(value)) {
      if (value.length) search.set(key, value.join(','))
      return
    }
    search.set(key, String(value))
  })

  const qs = search.toString()
  return qs ? `?${qs}` : ''
}

export async function fetchInventory(
  params: InventorySearchParams = {},
  signal?: AbortSignal,
): Promise<InventoryResponse> {
  const response = await fetch(
    `${API_BASE_URL}/api/inventory${toQuery({ ...params, includeInactive: true })}`,
    { signal },
  )
  if (!response.ok) {
    throw new Error(`Inventory request failed (${response.status})`)
  }
  return response.json()
}

export async function fetchInventoryById(
  id: number,
): Promise<{ data: InventoryItinerary }> {
  const response = await fetch(`${API_BASE_URL}/api/inventory/${id}`)
  const body = await response.json().catch(() => ({}))
  if (!response.ok) {
    throw new Error(body.message || `Inventory request failed (${response.status})`)
  }
  return body
}

export async function fetchBookings(
  params: {
    itineraryId?: number | string
  } = {},
  signal?: AbortSignal,
): Promise<{ count: number; data: InventoryBooking[] }> {
  const response = await fetch(
    `${API_BASE_URL}/api/bookings${toQuery({ itineraryId: params.itineraryId })}`,
    { signal },
  )
  const body = await response.json().catch(() => ({}))
  if (!response.ok) {
    throw new Error(body.message || `Bookings request failed (${response.status})`)
  }
  return body
}

export type UpdateBookingPassengerPayload = {
  id: number
  airlineRecordLocator?: string | null
  ticketNumber?: string | null
}

export async function updateBooking(
  bookingId: string,
  payload: { passengers: UpdateBookingPassengerPayload[] },
): Promise<{ data: InventoryBooking }> {
  const response = await fetch(
    `${API_BASE_URL}/api/bookings/${encodeURIComponent(bookingId)}`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    },
  )
  const body = await response.json().catch(() => ({}))
  if (!response.ok) {
    throw new Error(body.message || `Update booking failed (${response.status})`)
  }
  return body
}

export type SabreScheduleLeg = {
  marketingCarrier: string
  flightNumber: string
  operatingCarrier?: string
  operatingFlightNumber?: string
  origin: string
  destination: string
  departureDateTime: string
  arrivalDateTime: string
  originTerminal?: string
  destinationTerminal?: string
  duration?: string
  equipmentType?: string
  stops?: number
  layoverDuration?: string
}

export type SabreScheduleOption = {
  duration?: string
  connections?: number
  segments: SabreScheduleLeg[]
}

export async function fetchSabreFlightSchedules(params: {
  origin: string
  destination: string
  validatingCarrier: string
  departureDate: string
}): Promise<{ count: number; data: SabreScheduleOption[] }> {
  const response = await fetch(`${API_BASE_URL}/api/sabre/flight-schedules`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  })
  const body = await response.json().catch(() => ({}))
  if (!response.ok) {
    throw new Error(
      body.message || `Flight schedule request failed (${response.status})`,
    )
  }
  return body
}

export async function fetchFlightsData(params: {
  itineraryId?: number | string
  segmentId?: number | string
  departureAirport?: string
  arrivalAirport?: string
  carrier?: string
  flightNumber?: string
  departureDate?: string
} = {}): Promise<{
  count: number
  data: FlightsData[]
}> {
  const response = await fetch(
    `${API_BASE_URL}/api/flights-data${toQuery(params)}`,
  )
  if (!response.ok) {
    throw new Error(`Flights data request failed (${response.status})`)
  }
  return response.json()
}

export type FlightsDataPayload = {
  itineraryId?: number
  segmentId?: number
  flightOrder?: number
  flightNumber: string
  carrier: string
  operatingCarrier?: string
  operatingCarrierName?: string
  departureAirport: string
  departureDatetime: string
  arrivalAirport: string
  arrivalDatetime: string
  aircraftType?: string
  durationMin: number
}

export async function createFlightsData(
  payload: FlightsDataPayload,
): Promise<{ data: FlightsData }> {
  const response = await fetch(`${API_BASE_URL}/api/flights-data`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
  const body = await response.json().catch(() => ({}))
  if (!response.ok) {
    throw new Error(body.message || `Create flight failed (${response.status})`)
  }
  return body
}

export async function updateFlightsData(
  id: number,
  payload: Partial<FlightsDataPayload>,
): Promise<{ data: FlightsData }> {
  const response = await fetch(`${API_BASE_URL}/api/flights-data/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
  const body = await response.json().catch(() => ({}))
  if (!response.ok) {
    throw new Error(body.message || `Update flight failed (${response.status})`)
  }
  return body
}

export async function deleteFlightsData(
  id: number,
): Promise<{ data: { id: number } }> {
  const response = await fetch(`${API_BASE_URL}/api/flights-data/${id}`, {
    method: 'DELETE',
  })
  const body = await response.json().catch(() => ({}))
  if (!response.ok) {
    throw new Error(body.message || `Delete flight failed (${response.status})`)
  }
  return body
}

export async function createInventory(
  payload: CreateInventoryPayload,
): Promise<{ data: InventoryItinerary }> {
  const response = await fetch(`${API_BASE_URL}/api/inventory`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
  const body = await response.json().catch(() => ({}))
  if (!response.ok) {
    throw new Error(body.message || `Create failed (${response.status})`)
  }
  return body
}

export async function updateBlockActive(
  id: number,
  isActive: boolean,
): Promise<{ data: { id: number; is_active: boolean } }> {
  const response = await fetch(`${API_BASE_URL}/api/inventory/${id}/active`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ isActive }),
  })
  const body = await response.json().catch(() => ({}))
  if (!response.ok) {
    throw new Error(body.message || `Status update failed (${response.status})`)
  }
  return body
}

export async function cloneInventory(
  id: number,
): Promise<{ data: InventoryItinerary }> {
  const response = await fetch(`${API_BASE_URL}/api/inventory/${id}/clone`, {
    method: 'POST',
  })
  const body = await response.json().catch(() => ({}))
  if (!response.ok) {
    throw new Error(body.message || `Clone failed (${response.status})`)
  }
  return body
}

export async function deleteInventory(
  id: number,
): Promise<{ data: { id: number; orphanedBookings: number } }> {
  const response = await fetch(`${API_BASE_URL}/api/inventory/${id}`, {
    method: 'DELETE',
  })
  const body = await response.json().catch(() => ({}))
  if (!response.ok) {
    throw new Error(body.message || `Delete failed (${response.status})`)
  }
  return body
}

export async function updateInventory(
  id: number,
  payload: CreateInventoryPayload,
): Promise<{ data: InventoryItinerary }> {
  const response = await fetch(`${API_BASE_URL}/api/inventory/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
  const body = await response.json().catch(() => ({}))
  if (!response.ok) {
    throw new Error(body.message || `Update failed (${response.status})`)
  }
  return body
}
